import { TFunction } from 'next-i18next'
import { FormikErrors } from 'formik'

import { CssUnit } from '@/types'
import { RootState } from '@/lib/redux'
import { IconType } from '@/components/icons'

type UserProfile = {
  firstName: string
  lastName: string
  fullName: string
  username: string
  email: string
}

export type ContinueData = {
  first_name: string
  last_name: string
  username: string
  account_type: 'tenant' | 'landlord'
}

export type Step =
  | 'login'
  | 'existingEmail'
  | 'register'
  | 'existingSocialUser'
  | 'forgotPassword'
  | 'resetPasswordSuccess'
  | 'registerSavedAlertsNav'
  | 'registerAlert'
  | 'registerTenant'
export type DSLoginModalStep =
  | 'login'
  | 'existingEmail'
  | 'register'
  | 'registerTenant'
  | 'existingSocialUser'
  | 'forgotPassword'
  | 'resetPasswordSuccess'

export type LoginProps = {
  mode?: Step
  step?: ApplyStep
  afterLogin?: (s?: RootState['session'], step?: Step) => void
  asyncAfterLogin?: () => Promise<void>
  afterReset?: () => void
  redirectUrl?: string
  data?: Partial<ContinueData>
  lockField?: string[]
  eventId?: string
  defaultOperationType?: AccountType
  listingId?: number
  title?: React.ReactNode
  description?: React.ReactNode
  t: TFunction
  initialUsername?: string
  listing?: any
}
export type DSAuthModalProps = {
  mode?: DSLoginModalStep
  step?: ApplyStep
  afterReset?: () => void
  afterLogin?: () => void
  data?: Partial<ContinueData>
  eventId?: string
  defaultOperationType?: AccountType
  listingId?: number
  title?: React.ReactNode
  description?: React.ReactNode
  t: TFunction
  listing?: any
  visible: boolean
  onClose: () => void
  autoFocusName?: boolean
}

export type LoginStepProps = LoginProps & {
  setCurrentStep: (s: Step) => void
  close: () => void
  t: TFunction
}
export type DSLoginStepProps = DSAuthModalProps & {
  setCurrentStep: (s: Step) => void
  t: TFunction
  handleLoginAction: () => void
  onClose: () => void
  afterLogin2: () => void
}

interface ReactNodeArray extends Array<ReactNodeNoStrings> {}
export type ReactNodeNoStrings =
  | React.ReactElement
  | ReactNodeArray
  | boolean
  | null
  | undefined

export type StepProps = {
  component: React.ComponentType<LoginStepProps>
  title?: string
  width?: CssUnit
  height?: CssUnit
  banner?: boolean
  logo?: boolean
}
export type DSStepProps = {
  component: React.ComponentType<DSLoginStepProps>
  title?: string
  width?: CssUnit
  height?: CssUnit
  banner?: boolean
  logo?: boolean
  description: React.ReactNode
  illustration?: ReactNodeNoStrings
}

export type ApplyStep = 'stepOne' | 'stepTwo' | 'stepThree'

export type RegistrationStepProps = {
  setCurrentRegistrationStep: (s: ApplyStep) => void
  setCurrentStep: (s: Step) => void
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<{ [field: string]: any }>
  accountExists: boolean
  setAccountExists: (b: boolean) => void
  eventId?: string
  isSubmitting?: boolean
}

export type ApplyStepProps = {
  component: React.ComponentType<RegistrationStepProps>
}

type LoginGroupInfo = {
  name: string
  icon: IconType
  url: string
}

export type LoginGroup = {
  fb: LoginGroupInfo
  apple: LoginGroupInfo
  google: LoginGroupInfo
}

export type Provider = keyof LoginGroup | 'email'

export type SocialButtonsProps = {
  onClick: (i: Provider, g: LoginGroupInfo, e: React.MouseEvent) => void
  trackingId: string
}

type SocialData = {
  access_token?: string
  invited_user?: unknown
  message?: string
  message_key?: string
  new?: number
  provider?: Provider
  source?: string
  source_message?: string
  token?: string
  user_profile: Partial<UserProfile>
  authorization_code: string
}

export type Social = {
  platform?: Provider
  data?: SocialData
}

export type Username = string

export enum AccountType {
  LANDLORD = 'landlord',
  TENANT = 'tenant'
}

export type FormProps = {
  token?: string
  social_login?: string
  username?: string
  full_name?: string
  password?: string
  password_confirm?: string
  account_type?: AccountType
  authorizationCode?: string
  first_name?: string
  last_name?: string
  email?: string
  captcha?: string
}
